import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { Bell, LogOut, Mail } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Brand } from '@/design-system/components/primitives/brand'

/** Иконочная кнопка шапки (конвертик, колокольчик, выход) — общие токены оформления. */
const ICON_BUTTON_CLASS =
  'inline-flex items-center justify-center rounded-8 p-2 text-txt-secondary outline-none transition-colors hover:bg-hover hover:text-txt focus-visible:ring-2 focus-visible:ring-accent'

/**
 * Topbar — верхняя панель кабинета (`.topbar` макета): бренд, блок организации, блок пользователя,
 * «конвертик» вопросов (client-questions, ТЗ п.9.7: «рядом с колокольчиком… точкой непрочитанные
 * ответы»; показывается, если передан `onMessagesClick`), «колокольчик» уведомлений и кнопка выхода. Высота/цвета из токенов; колокольчик и выход —
 * доступные с клавиатуры кнопки. Кнопка выхода (centrika-docs!58) — крайний правый элемент шапки.
 */
export interface TopbarProps extends HTMLAttributes<HTMLElement> {
  /** Слот блока организации (например, CompanyBadge + название). */
  company?: ReactNode
  /** Слот блока пользователя (например, Avatar + имя). */
  user?: ReactNode
  onBellClick?: () => void
  bellLabel?: string
  /** Признак непрочитанных уведомлений (точка на колокольчике). */
  hasUnread?: boolean
  /** «Конвертик» — переход к вопросам; без обработчика кнопки нет. */
  onMessagesClick?: () => void
  messagesLabel?: string
  /** Точка на конвертике — есть непрочитанные ответы. */
  hasUnreadMessages?: boolean
  /** Обработчик выхода из системы; кнопка показывается только если передан (centrika-docs!58). */
  onLogout?: () => void
  /** Доступное название кнопки выхода (текст на кнопке не отображается). */
  logoutLabel?: string
}

export const Topbar = forwardRef<HTMLElement, TopbarProps>(
  (
    {
      className,
      company,
      user,
      onBellClick,
      bellLabel = 'Уведомления',
      hasUnread,
      onMessagesClick,
      messagesLabel = 'Мои вопросы',
      hasUnreadMessages,
      onLogout,
      logoutLabel = 'Выйти',
      ...props
    },
    ref,
  ) => (
    <header
      ref={ref}
      className={cn(
        'sticky top-0 z-sticky flex h-topbar items-center justify-between border-b border-line bg-white px-4',
        className,
      )}
      {...props}
    >
      <div className="flex items-center">
        <Brand size="sm" />
        {company ? (
          <div className="ml-3.5 flex items-center gap-2.5 border-l border-line pl-3.5">
            {company}
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-2.5">
        {onMessagesClick ? (
          <button
            type="button"
            onClick={onMessagesClick}
            aria-label={hasUnreadMessages ? `${messagesLabel}: есть новые ответы` : messagesLabel}
            title={messagesLabel}
            className={cn('relative', ICON_BUTTON_CLASS)}
          >
            <Mail className="size-5 stroke-current" strokeWidth={1.7} aria-hidden />
            {hasUnreadMessages ? (
              <span
                aria-hidden
                data-testid="messages-unread-dot"
                className="absolute right-1.5 top-1.5 size-2 rounded-round bg-red"
              />
            ) : null}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onBellClick}
          aria-label={bellLabel}
          title={bellLabel}
          className={cn('relative', ICON_BUTTON_CLASS)}
        >
          <Bell className="size-5 stroke-current" strokeWidth={1.7} aria-hidden />
          {hasUnread ? (
            <span aria-hidden className="absolute right-1.5 top-1.5 size-2 rounded-round bg-red" />
          ) : null}
        </button>
        {user}
        {onLogout ? (
          <button
            type="button"
            onClick={onLogout}
            aria-label={logoutLabel}
            title={logoutLabel}
            className={ICON_BUTTON_CLASS}
          >
            <LogOut className="size-5 stroke-current" strokeWidth={1.7} aria-hidden />
          </button>
        ) : null}
      </div>
    </header>
  ),
)
Topbar.displayName = 'Topbar'
